import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
    useTheme,
} from "@mui/material";
import ReactPlayer from "react-player";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Fetch from "../../services/Fetch";
import AuthContext from "../../context/AuthContext";
import SnackbarAlert from "../../components/SnackBar";
import useSnackBar from "../../hooks/UseSnackBar";
import PageHero from "../../components/motion/PageHero";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import ScrollReveal from "../../components/motion/ScrollReveal";
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import OndemandVideoOutlinedIcon from '@mui/icons-material/OndemandVideoOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { useConstants } from "../../hooks/UseConstants";
import { useRequireAuth } from "../../hooks/useRequireAuth";
import { resolveCourseImage, resolveMediaUrl } from "../../helper/resolveMediaUrl";
import { formatCurrency } from "../../helper/formatCurrency";

function CourseDetail() {
    const { host, language } = useConstants();
    const isAr = language === 'ar';
    const theme = useTheme();
    const { id } = useParams();
    const { user, refreshUser } = useContext(AuthContext);
    const requireAuth = useRequireAuth();
    const navigate = useNavigate();
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const [loading, setLoading] = useState(true);
    const [course, setCourse] = useState(null);
    const [buying, setBuying] = useState(false);
    const [descOpen, setDescOpen] = useState(false);
    const [progress, setProgress] = useState(null);

    const load = async () => {
        setLoading(true);
        const result = await Fetch(`${host}/courses/${id}/show`, 'GET');
        if (result.status === 200) setCourse(result.data.data);
        setLoading(false);
    };

    useEffect(() => { load(); }, [host, id]);

    const owned = !!(course?.my_enrollment?.id ?? course?.my_enrollment);
    const teacher = course?.teacher;
    const teacherName = teacher ? `${teacher.first_name || ''} ${teacher.last_name || ''}`.trim() : '';
    const desc = isAr
        ? (course?.description_ar || course?.description_en || '')
        : (course?.description_en || course?.description_ar || '');
    const wallet = Number(user?.wallet?.balance ?? 0);
    const price = Number(course?.effective_price ?? course?.price ?? 0);
    const listPrice = Number(course?.price ?? 0);
    const objectivesText = isAr
        ? (course?.objectives_ar || course?.objectives_en || '')
        : (course?.objectives_en || course?.objectives_ar || '');
    const objectives = objectivesText.split('\n').filter(Boolean);
    const prerequisites = isAr
        ? (course?.prerequisites_ar || course?.prerequisites_en || '')
        : (course?.prerequisites_en || course?.prerequisites_ar || '');
    const courseLevel = isAr
        ? (course?.level_ar || course?.level_en || '')
        : (course?.level_en || course?.level_ar || '');
    const courseImage = resolveCourseImage(course);
    const introVideo = resolveMediaUrl(course?.intro_video || '');
    const courseTitle = isAr ? (course?.name_ar || course?.name_en) : (course?.name_en || course?.name_ar);
    const learningCount = useMemo(
        () => (course?.contents || []).filter((item) => item.content_type !== 'Exam').length,
        [course?.contents]
    );
    const examCount = useMemo(
        () => (course?.contents || []).filter((item) => item.content_type === 'Exam').length,
        [course?.contents]
    );
    const completedIds = useMemo(
        () => new Set(progress?.completed_content_ids || []),
        [progress]
    );

    useEffect(() => {
        const loadProgress = async () => {
            if (!owned) {
                setProgress(null);
                return;
            }
            const result = await Fetch(`${host}/student/courses/${id}/progress`, 'GET');
            if (result.status === 200) {
                setProgress(result.data.data);
            }
        };
        loadProgress();
    }, [host, id, owned]);

    const buy = async () => {
        if (!requireAuth()) return;
        if (!course?.is_free && wallet < price) {
            setSnackBar('error', 'رصيدك غير كافٍ. يرجى شحن المحفظة أولاً');
            setTimeout(() => navigate('/wallet'), 1500);
            return;
        }
        setBuying(true);
        const result = await Fetch(`${host}/student/buy/course/${id}`, 'POST', {});
        if (result.status === 200) {
            setSnackBar('success', 'تم شراء الدورة بنجاح');
            await refreshUser();
            await load();
        } else {
            const msg = result.data?.errors?.[0] || result.data?.message || 'تعذر الشراء';
            setSnackBar('error', msg);
            if (msg.toLowerCase().includes('wallet') || msg.includes('رصيد')) navigate('/wallet');
        }
        setBuying(false);
    };

    if (loading) return <Box className="flex justify-center py-20"><CircularProgress /></Box>;
    if (!course) return <Typography>{isAr ? 'الدورة غير موجودة' : 'Course not found'}</Typography>;

    const contents = course.contents || [];

    return (
        <Box>
            <PageHero
                title={courseTitle}
                subtitle={teacherName ? (isAr ? `المدرس: ${teacherName}` : `Instructor: ${teacherName}`) : undefined}
                icon={<MenuBookOutlinedIcon />}
            />
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '1.35fr 0.95fr' },
                    gap: 3,
                    alignItems: 'start',
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <ScrollReveal>
                        <DashboardPanelCard
                            title={isAr ? 'معاينة الدورة' : 'Course preview'}
                            subtitle={isAr ? 'صورة الدورة والفيديو التعريفي إن وجد' : 'Course image and intro video if available'}
                            className="mx-0 mt-0"
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        minHeight: { xs: 220, md: 320 },
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                        background: 'linear-gradient(135deg, #0D358C 0%, #2563eb 45%, #457b9d 100%)',
                                    }}
                                >
                                    {courseImage ? (
                                        <Box
                                            component="img"
                                            src={courseImage}
                                            alt={courseTitle}
                                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#fff',
                                                gap: 1,
                                            }}
                                        >
                                            <ImageOutlinedIcon sx={{ fontSize: 52 }} />
                                            <Typography fontWeight={700}>
                                                {isAr ? 'لا توجد صورة للدورة' : 'No course image'}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            insetInline: 0,
                                            bottom: 0,
                                            p: 2,
                                            background: 'linear-gradient(to top, rgba(4,14,38,0.72), transparent)',
                                            color: '#fff',
                                        }}
                                    >
                                        <Typography variant="h6" fontWeight={800}>
                                            {courseTitle}
                                        </Typography>
                                        {teacherName ? (
                                            <Typography variant="body2" sx={{ opacity: 0.92 }}>
                                                {isAr ? `بواسطة ${teacherName}` : `By ${teacherName}`}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                </Box>

                                {introVideo ? (
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#0D358C', mb: 1 }}>
                                            {isAr ? 'الفيديو التعريفي' : 'Intro video'}
                                        </Typography>
                                        <Box sx={{ borderRadius: 3, overflow: 'hidden', bgcolor: '#000' }}>
                                            <ReactPlayer url={introVideo} controls width="100%" height={320} />
                                        </Box>
                                    </Box>
                                ) : null}
                            </Box>
                        </DashboardPanelCard>
                    </ScrollReveal>

                    {desc ? (
                        <ScrollReveal delay={80}>
                            <DashboardPanelCard
                                title={isAr ? 'نبذة عن الدورة' : 'About this course'}
                                subtitle={isAr ? 'وصف مختصر لمحتوى الدورة' : 'A concise overview of the course'}
                                className="mx-0 mt-0"
                            >
                                <Collapse in={descOpen} collapsedSize={72}>
                                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.9 }}>
                                        {desc}
                                    </Typography>
                                </Collapse>
                                {desc.length > 140 ? (
                                    <Button size="small" sx={{ mt: 1 }} onClick={() => setDescOpen(!descOpen)}>
                                        {descOpen
                                            ? (isAr ? 'عرض أقل' : 'Show less')
                                            : (isAr ? 'قراءة المزيد' : 'Read more')}
                                    </Button>
                                ) : null}
                            </DashboardPanelCard>
                        </ScrollReveal>
                    ) : null}

                    {objectives.length > 0 ? (
                        <ScrollReveal delay={120}>
                            <DashboardPanelCard
                                title={isAr ? 'ما الذي ستتعلمه؟' : 'What you will learn'}
                                subtitle={isAr ? 'أهداف ومخرجات الدورة' : 'Course goals and outcomes'}
                                className="mx-0 mt-0"
                            >
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.25 }}>
                                    {objectives.map((line, i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 1,
                                                p: 1.25,
                                                borderRadius: 2,
                                                bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#f8fafc',
                                                border: '1px solid',
                                                borderColor: theme.palette.mode === 'dark' ? 'divider' : '#e2e8f0',
                                            }}
                                        >
                                            <TaskAltOutlinedIcon sx={{ color: '#16a34a', mt: '2px', fontSize: 18 }} />
                                            <Typography variant="body2">{line}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </DashboardPanelCard>
                        </ScrollReveal>
                    ) : null}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <ScrollReveal delay={40}>
                        <DashboardPanelCard
                            title={isAr ? 'معلومات الدورة' : 'Course details'}
                            subtitle={isAr ? 'السعر والمدرس والمستوى ومحتوى الدورة' : 'Price, instructor, level, and course stats'}
                            className="mx-0 mt-0"
                        >
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                <Chip
                                    icon={<AutoStoriesOutlinedIcon />}
                                    label={`${isAr ? 'الدروس' : 'Lessons'}: ${learningCount}`}
                                    sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700 }}
                                />
                                <Chip
                                    icon={<AssignmentOutlinedIcon />}
                                    label={`${isAr ? 'الاختبارات' : 'Exams'}: ${examCount}`}
                                    sx={{ bgcolor: '#f8fafc', color: '#475569', fontWeight: 700 }}
                                />
                                {courseLevel ? (
                                    <Chip
                                        icon={<SchoolOutlinedIcon />}
                                        label={`${isAr ? 'المستوى' : 'Level'}: ${courseLevel}`}
                                        sx={{ bgcolor: '#f0fdf4', color: '#166534', fontWeight: 700 }}
                                    />
                                ) : null}
                                {owned && progress?.progress_percentage != null ? (
                                    <Chip
                                        icon={<TaskAltOutlinedIcon />}
                                        label={`${isAr ? 'التقدّم' : 'Progress'}: ${progress.progress_percentage}%`}
                                        sx={{ bgcolor: '#fff7ed', color: '#9a3412', fontWeight: 700 }}
                                    />
                                ) : null}
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                {course.is_free ? (
                                    <Typography variant="h5" fontWeight={800} color="success.main">
                                        {isAr ? 'مجاني' : 'Free'}
                                    </Typography>
                                ) : course.has_active_discount ? (
                                    <>
                                        <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                            {formatCurrency(listPrice, isAr)}
                                        </Typography>
                                        <Typography variant="h5" fontWeight={800} color="primary">
                                            {formatCurrency(price, isAr)}
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography variant="h5" fontWeight={800} color="primary">
                                        {formatCurrency(price, isAr)}
                                    </Typography>
                                )}
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {teacherName ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                        <PersonOutlineOutlinedIcon sx={{ color: '#0D358C' }} />
                                        <Typography variant="body2" sx={{ flex: 1 }}>
                                            {teacherName}
                                        </Typography>
                                        {teacher?.id ? (
                                            <Button
                                                size="small"
                                                onClick={() => navigate(`/teachers/${teacher.id}`, { state: { teacher } })}
                                            >
                                                {isAr ? 'ملف المدرس' : 'Profile'}
                                            </Button>
                                        ) : null}
                                    </Box>
                                ) : null}

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                    <ShoppingCartOutlinedIcon sx={{ color: '#0D358C' }} />
                                    <Typography variant="body2">
                                        {isAr ? 'رصيدك في المحفظة' : 'Your wallet balance'}: {formatCurrency(wallet, isAr)}
                                    </Typography>
                                </Box>

                                {prerequisites ? (
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                                        <SchoolOutlinedIcon sx={{ color: '#0D358C', mt: '2px' }} />
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {isAr ? 'المتطلبات' : 'Prerequisites'}: {prerequisites}
                                        </Typography>
                                    </Box>
                                ) : null}

                                {introVideo ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                        <OndemandVideoOutlinedIcon sx={{ color: '#0D358C' }} />
                                        <Typography variant="body2">
                                            {isAr ? 'يتوفر فيديو تعريفي لهذه الدورة' : 'This course includes an intro video'}
                                        </Typography>
                                    </Box>
                                ) : null}
                            </Box>

                            <Button
                                variant="contained"
                                fullWidth
                                disabled={buying}
                                onClick={owned ? () => {
                                    if (!requireAuth()) return;
                                    const firstFile = contents.find(c => c.content_type !== 'Exam' && c.content?.id);
                                    if (firstFile) navigate(`/courses/${id}/lesson/${firstFile.content.id}`);
                                } : buy}
                                sx={{ mt: 3, py: 1.2, fontWeight: 700 }}
                            >
                                {buying ? (
                                    <CircularProgress size={22} color="inherit" />
                                ) : owned ? (
                                    isAr ? 'ابدأ التعلم' : 'Start learning'
                                ) : course.is_free ? (
                                    isAr ? 'الاشتراك مجاناً' : 'Join for free'
                                ) : user ? (
                                    isAr ? 'شراء الدورة' : 'Buy course'
                                ) : (
                                    isAr ? 'تسجيل الدخول للشراء' : 'Login to purchase'
                                )}
                            </Button>
                        </DashboardPanelCard>
                    </ScrollReveal>

                    <ScrollReveal delay={100}>
                        <DashboardPanelCard
                            title={isAr ? 'محتوى الدورة' : 'Course content'}
                            subtitle={isAr ? 'الدروس والاختبارات المتاحة داخل الدورة' : 'Lessons and exams included in this course'}
                            className="mx-0 mt-0"
                        >
                            <List sx={{ p: 0 }}>
                                {contents.map(item => {
                                    const isExam = item.content_type === 'Exam';
                                    const isVideoLesson = !isExam && Boolean(item.content?.is_video);
                                    const isCompleted = completedIds.has(item.id);
                                    const title = isAr
                                        ? (item.content?.name_ar || item.content?.name_en || (isExam ? 'اختبار' : 'درس'))
                                        : (item.content?.name_en || item.content?.name_ar || (isExam ? 'Exam' : 'Lesson'));
                                    const canOpen = owned && item.content?.id;

                                    return (
                                        <ListItem key={item.id} disablePadding sx={{ mb: 1 }}>
                                            <ListItemButton
                                                disabled={!canOpen}
                                                sx={{
                                                    borderRadius: 2,
                                                    border: '1px solid',
                                                    borderColor: theme.palette.mode === 'dark' ? 'divider' : '#e2e8f0',
                                                    bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#f8fafc',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 2,
                                                }}
                                                onClick={() => {
                                                    if (isExam && owned) navigate(`/course/${id}/exam/${item.content.id}`);
                                                    else if (!isExam && owned && item.content?.id) navigate(`/courses/${id}/lesson/${item.content.id}`);
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.25,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={title}
                                                        secondary={isExam
                                                            ? (isAr ? 'اختبار' : 'Exam')
                                                            : (isAr ? 'درس / ملف' : 'Lesson / file')}
                                                        sx={{ m: 0, textAlign: 'right' }}
                                                        slotProps={{
                                                            primary: { sx: { textAlign: 'right', fontWeight: 600 } },
                                                            secondary: { sx: { textAlign: 'right' } },
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            width: 36,
                                                            height: 36,
                                                            borderRadius: 2,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0,
                                                            bgcolor: isExam ? '#eff6ff' : '#f0fdf4',
                                                            color: isExam ? '#1d4ed8' : '#166534',
                                                        }}
                                                    >
                                                        {isExam
                                                            ? <AssignmentOutlinedIcon fontSize="small" />
                                                            : isVideoLesson
                                                                ? <PlayCircleOutlineIcon fontSize="small" />
                                                                : <PictureAsPdfOutlinedIcon fontSize="small" />}
                                                    </Box>
                                                </Box>
                                                <Box sx={{ flexShrink: 0 }}>
                                                    {isCompleted ? (
                                                        <Chip
                                                            size="small"
                                                            icon={<TaskAltOutlinedIcon />}
                                                            label={isAr ? 'مكتمل' : 'Completed'}
                                                            sx={{ bgcolor: '#f0fdf4', color: '#166534' }}
                                                        />
                                                    ) : !owned ? (
                                                        <Chip
                                                            size="small"
                                                            icon={<LockOutlinedIcon />}
                                                            label={isAr ? 'مغلق' : 'Locked'}
                                                            sx={{ bgcolor: '#fff7ed', color: '#9a3412' }}
                                                        />
                                                    ) : null}
                                                </Box>
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </DashboardPanelCard>
                    </ScrollReveal>
                </Box>
            </Box>
            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </Box>
    );
}

export default CourseDetail;
